/**
 * The social feed wire is USD minor units on every path (see the realtime
 * social_event mapper: amount_usd / potential_payout_usd). This resolves the
 * factor that turns a decoded USD display value into the VIEWER's currency.
 *
 * Units per 1 USD — same direction as GameConfig.usdRate in marbles-stack-go
 * and as CurrencyRateService.GetCustomCurrencyRate (GLD = 10000).
 *
 * Returns 1 when nothing is known so callers multiply unconditionally: 1 means
 * "render the USD figure as-is", which is exactly the pre-conversion
 * behaviour. That is the rollout kill-switch — a config with no usdRate
 * behaves identically to the feed before this feature existed.
 */

// Policy-fixed sweepstakes rates. These coins have no market rate; Go returns
// them from GetCustomCurrencyRate, so a healthy config already carries them.
// Retained as a fallback for a config generated before usdRate shipped (the
// GameConfig cache holds entries for up to 24h) and to keep sweeps feeds
// correct if a rate lookup ever fails.
//
// Mirrors customFiatCurrencies in marbles-stack-go
// internal/game/application/services/currency_rate.go — any change there
// should be mirrored here, though the server rate now takes precedence.
const SWEEPSTAKES_USD_RATES: Record<string, number> = {
  GLD: 10000,
  SS1: 1,
  BC1: 1, // Blitz BlitzCoin
  XXX: 1,
  UFC: 1,
  CVD: 1, // Coverd Cash
  WOW: 100, // WowVegas GoldCoin
  GC: 100, // Rolla/Csno GoldCoin
  PGSC: 1, // SoftSwiss Sweepstake Coin
  PGGC: 10000, // SoftSwiss Gold Coin
  EEGP: 1, // Rainbet Cash — 1:1 with USD (renders as RC)
  EPGP: 100, // Rainbet Gold Coin — 100 coins = 1 USD (renders as GC)
  CAPY: 1, // Capyspin Sweep Coin — 1:1 with USD (renders as CC)
  CAOC: 100 // Capyspin Gold Coin — 100 coins = 1 USD (renders as OC)
}

export interface SocialUsdRate {
  /** Units of the viewer's currency per 1 USD. 1 when no conversion applies. */
  rate: number
  /**
   * True only when a REAL conversion factor resolved (server rate or the
   * policy-fixed sweeps table). Gates the currency SYMBOL as well as the
   * arithmetic: symbol and value must come from this one decision, or an
   * excluded currency (crypto phase 1) renders its own symbol over USD
   * numbers — the exact mislabel bug class this feature exists to fix.
   */
  converted: boolean
}

export const getSocialUsdRate = (
  currency: string | undefined,
  usdRate: number | undefined
): SocialUsdRate => {
  if (typeof usdRate === 'number' && Number.isFinite(usdRate) && usdRate > 0) {
    return { rate: usdRate, converted: true }
  }
  // hasOwnProperty.call, NOT Object.hasOwn: the widget has no browserslist
  // config, so Next's default floor includes Safari < 15.4 where Object.hasOwn
  // throws — on the live feed path, with widget RUM off (an invisible crash).
  if (currency && Object.prototype.hasOwnProperty.call(SWEEPSTAKES_USD_RATES, currency)) {
    const rate = SWEEPSTAKES_USD_RATES[currency]
    // hasOwnProperty already proved the key is an own property (an inherited
    // key like 'toString' must NOT match); this check only narrows the indexed
    // type under noUncheckedIndexedAccess.
    if (rate !== undefined) {
      return { rate, converted: true }
    }
  }
  return { rate: 1, converted: false }
}
